import React from 'react'
import { useLocation } from 'react-router-dom'
import Flex from '@eversports/react-components/primitives/Flex'
import Text from '@eversports/react-components/primitives/Text'
import MonetaryValue from '@eversports/react-components/primitives/MonetaryValue'
import Processing from '@eversports/react-components/primitives/Processing'

import { useCorporateInfoItemCartQuery } from '../../graphql'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import { Localized } from '../../localization/react'
import { PriceTableItemBudget } from '../../typings/PriceTable'

interface Props {
  item: PriceTableItemBudget
}

const CorporateBudgetItem = ({ item }: Props) => {
  const location = useLocation()
  const { cartId, orderId } = getPhoenixUrlParams(location.search)
  const hideCorporateBudgetItem = Boolean(orderId)

  const { data, loading } = useCorporateInfoItemCartQuery({
    variables: { cartId: cartId as string },
    skip: hideCorporateBudgetItem || !cartId,
  })

  if (hideCorporateBudgetItem) {
    return null
  }

  const availableBudget = data?.cart.availableBudget
  const isBudgetDepleted = availableBudget?.employeeBudgetDepleted || availableBudget?.totalBudgetDepleted
  const corporatePartnerName = data?.cart.availableCorporateBenefits?.corporatePartner?.name

  if (!availableBudget || !corporatePartnerName) {
    return null
  }

  return (
    <Processing isLoading={loading}>
      <Flex justifyContent="space-between">
        {item ? (
          <>
            <div>
              <Flex horizontalSpacing="dense" alignItems="center">
                <Text>
                  <Localized id="corporate-pricing-budget-label" params={{ corporatePartnerName }} />
                </Text>
              </Flex>
            </div>
            {item.totalPrice && (
              <Text fontWeight="bold">
                <MonetaryValue value={item.totalPrice.value} currencyCode={item.totalPrice.currency} />
              </Text>
            )}
          </>
        ) : (
          <>
            <div>
              <Flex horizontalSpacing="dense" alignItems="center">
                <Text color={isBudgetDepleted ? 'text.disabled' : 'text.primary'}>
                  <Localized id="corporate-pricing-budget-label" params={{ corporatePartnerName }} />
                </Text>
              </Flex>
              {availableBudget.employeeBudgetDepleted ? (
                <Text size="x-small" color="danger">
                  <Localized id="corporate-budget-employee-budget-depleted" />
                </Text>
              ) : (
                availableBudget.totalBudgetDepleted && (
                  <Text size="x-small" color="danger">
                    <Localized id="corporate-budget-total-budget-depleted" />
                  </Text>
                )
              )}
            </div>
            <Text color={isBudgetDepleted ? 'text.disabled' : 'text.primary'}>-</Text>
          </>
        )}
      </Flex>
    </Processing>
  )
}

export default CorporateBudgetItem
